import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Typography,
  DatePicker,
  message,
} from "antd";
import { apiRequest } from "../../../service/apiRequest";
import { showSuccess, showError } from "../../../components/errorHandler";

const { Title } = Typography;
const { TextArea } = Input;

const inputStyle = { height: "45px" };
const textAreaStyle = { height: "67.5px" };

const QuickRegistration: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // ✅ Form Submit
  const handleSubmitLedger = async (values: any) => {
    try {
      setLoading(true);

      const payload = {
        ledger_first_name: values.ledger_first_name,
        ledger_last_name: values.ledger_last_name,
        ledger_mobile_no: values.ledger_mobile_no,
        ledger_email: values.ledger_email,
        ledger_dob: values.birthday,
        ledger_marriage_anniversary: values.anniversary,
        ledger_offer_festival: values.ledger_offer_festival,
        ledger_billing_address: values.billing_address,
        ledger_company_name: values.ledger_company_name,
        ledger_company_ledger_no: values.ledger_company_ledger_no,
        ledger_company_address: values.company_address,
        ledger_company_mobile: values.ledger_company_mobile,
      };

      const response = await apiRequest({
        method: "post",
        url: "/add-quick-registration",
        data: payload,
      });

      showSuccess("Ledger created successfully!");
      console.log("Submitted Payload:", payload);
      form.resetFields();
    } catch (error) {
      showError("Failed to create ledger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row style={{ minHeight: "100vh" }} align="middle" justify="center">
      <Col xs={22} sm={20} md={16} lg={10}>
        <div style={{ background: "#fff", borderRadius: 8 }}>
          <div style={{ textAlign: "center", paddingTop: "1rem" }}>
            <img src="/LOGO_1.png" alt="Logo" style={{ maxWidth: "120px" }} />
          </div>
          <div style={{ textAlign: "center" }}>
            <Title level={3}>Quick Client Registration Form</Title>
          </div>

          <Form layout="vertical" form={form} onFinish={handleSubmitLedger}>
            {/* ✅ Mobile Number */}
            <Form.Item
              label="Mobile Number"
              name="ledger_mobile_no"
              rules={[
                { required: true, message: "Mobile number is required" },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Enter a valid 10-digit mobile number",
                },
              ]}
            >
              <Input
                placeholder="Enter mobile number"
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]*"
                style={inputStyle}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={async (e) => {
                  const enteredNumber = e.target.value;
                  try {
                    const res = await apiRequest({
                      method: "get",
                      url: "/check-mobile?mobile_no="+enteredNumber,
                    });
                    if (res.exists) {
                      form.setFields([
                        {
                          name: "ledger_mobile_no",
                          errors: ["This number is already registered"],
                        },
                      ]);
                    }
                  } catch (error) {
                    console.error("Mobile check error:", error);
                  }
                }}
                onChange={() => {
                  form.setFields([{ name: "ledger_mobile_no", errors: [] }]);
                }}
              />
            </Form.Item>

            {/* ✅ First Name */}
            <Form.Item
              label="First Name"
              name="ledger_first_name"
              rules={[
                { required: true, message: "First name is required" },
                {
                  pattern: /^[A-Za-z ]+$/,
                  message: "Only letters are allowed",
                },
              ]}
            >
              <Input
                placeholder="Enter your first name"
                style={inputStyle}
                onKeyPress={(e) => {
                  if (!/[a-zA-Z ]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            {/* ✅ Last Name */}
            <Form.Item
              label="Last Name"
              name="ledger_last_name"
              rules={[
                {
                  pattern: /^[A-Za-z ]*$/,
                  message: "Only letters are allowed",
                },
              ]}
            >
              <Input placeholder="Enter your last name" style={inputStyle} />
            </Form.Item>

            {/* ✅ Email */}
            <Form.Item
              label="Email Address"
              name="ledger_email"
              rules={[{ type: "email", message: "Enter a valid email" }]}
            >
              <Input placeholder="Enter your email" style={inputStyle} />
            </Form.Item>

            {/* ✅ Billing Address */}
            <Form.Item label="Billing Address" name="billing_address">
              <TextArea placeholder="Billing address" style={textAreaStyle} />
            </Form.Item>

            {/* ✅ Birthday */}
            <Form.Item label="Birthday" name="birthday">
              <DatePicker style={{ ...inputStyle, width: "100%" , borderRadius: "20px" }} />
            </Form.Item>

            {/* ✅ Anniversary */}
            <Form.Item label="Anniversary" name="anniversary">
              <DatePicker style={{ ...inputStyle, width: "100%" , borderRadius: "20px" }} />
            </Form.Item>

            {/* ✅ Company Address */}
            <Form.Item label="Company Address" name="company_address">
              <TextArea
                placeholder="Company address"
                style={textAreaStyle}
              />
            </Form.Item>

            {/* ✅ GSTIN */}
            <Form.Item
              label="GSTIN Number"
              name="ledger_company_ledger_no"
              rules={[
                {
                  max: 15,
                  message: "GST number cannot be more than 15 characters",
                },
                {
                  pattern: /^[A-Z0-9]{1,15}$/,
                  message: "Only capital letters and numbers allowed",
                },
              ]}
            >
              <Input
                placeholder="Enter GST number"
                maxLength={15}
                style={inputStyle}
                onKeyPress={(e) => {
                  if (!/[A-Z0-9]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
            </Form.Item>

            {/* ✅ Company Mobile */}
            <Form.Item
              label="Company Mobile"
              name="ledger_company_mobile"
              rules={[
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Enter a valid 10-digit mobile number",
                },
              ]}
            >
              <Input
                placeholder="Company mobile number"
                maxLength={10}
                style={inputStyle}
              />
            </Form.Item>

            {/* ✅ Submit */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{
                  width: "50%",
                  padding: "12px",
                  fontSize: "18px",
                }}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default QuickRegistration;
